import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Award, BookOpen, Users, ArrowRight, Facebook } from "lucide-react";
import { BRAND, IMG, SOCIAL } from "@/lib/content";

const BULLETS = [
  { icon: Award, text: "15+ years with higher education institutions in Poland" },
  { icon: BookOpen, text: "Deep knowledge of rankings, evaluations and institutional fit" },
  { icon: Users, text: "Thousands of students and families advised through high-stakes decisions" },
];

export default function AboutExpert() {
  return (
    <section className="section-pad" data-testid="home-about">
      <div className="container-sp grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-5"
        >
          <div className="relative rounded-[2rem] overflow-hidden border border-[#E5E3D8]">
            <img src={IMG.krzWithFlag} alt={BRAND.expertName} className="w-full h-[560px] object-cover" />
          </div>
          <div className="mt-4 text-sm text-[#718096] font-mono tracking-wide">
            {BRAND.expertName} · {BRAND.expertTitle}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="lg:col-span-7"
        >
          <div className="overline">Meet your expert</div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#0D1B2A] mt-3 leading-tight">
            Meet your Poland higher education expert.
          </h2>
          <p className="mt-6 text-[#4A5568] leading-relaxed max-w-xl">
            For more than fifteen years, we've worked with Polish higher education
            institutions — reviewing programs, understanding rankings and evaluations,
            and guiding international students through the full path from first
            question to residence card.
          </p>
          <p className="mt-4 text-[#4A5568] leading-relaxed max-w-xl">
            Our mission is simple: be the most trusted and cited expert on
            international student matters in Poland — by being genuinely useful,
            specific, and honest with every family we work with.
          </p>

          <div className="mt-8 space-y-3">
            {BULLETS.map((b) => (
              <div key={b.text} className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-[#EBE9E1] flex items-center justify-center shrink-0">
                  <b.icon className="w-4 h-4 text-[#D7141A]" />
                </div>
                <p className="text-sm text-[#0D1B2A] pt-1.5 leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>

          <Link to="/about" className="btn-outline mt-10" data-testid="home-about-cta">
            Read the full story <ArrowRight className="w-4 h-4" />
          </Link>

          <div className="mt-8 pt-8 border-t border-[#EBE9E1]">
            <div className="overline mb-3">Find me in the community</div>
            <div className="flex flex-wrap gap-3">
              <a
                href={SOCIAL.facebook}
                target="_blank"
                rel="noreferrer"
                data-testid="about-expert-facebook"
                className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-full bg-white border border-[#EFEEE8] hover:border-[#D7141A]/40 text-sm text-[#0D1B2A] transition-all"
              >
                <Facebook className="w-4 h-4 text-[#1877F2]" />
                Facebook · StudyPoland.Info
              </a>
              <a
                href={SOCIAL.tiktok}
                target="_blank"
                rel="noreferrer"
                data-testid="about-expert-tiktok"
                className="inline-flex items-center gap-2.5 px-4 py-2.5 rounded-full bg-white border border-[#EFEEE8] hover:border-[#D7141A]/40 text-sm text-[#0D1B2A] transition-all"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.21a8.27 8.27 0 0 0 4.83 1.54V6.3a4.85 4.85 0 0 1-1.9-.61Z" />
                </svg>
                TikTok · @krzysztof_szymanski
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
